"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import { LogIn, UserPlus } from "lucide-react";

import { MissingInfoSection } from "@/components/sections/missing-info-section";
import { PropertyInquiryForm } from "@/components/forms/property-inquiry-form";
import {
  buildPrefillMessage,
  type MissingInfoItem,
  type MissingInfoKey,
} from "@/lib/projects/missing-info";

interface Props {
  projectSlug: string;
  projectTitle: string;
  missingItems: readonly MissingInfoItem[];
  /**
   * Authenticated buyer context, threaded from the page server
   * component. When null, the inquiry form is replaced by a
   * sign-in / sign-up CTA card so anonymous visitors must create an
   * account before submitting. When present, name + email pre-fill
   * the form.
   */
  currentUser: { email: string; name: string | null } | null;
}

/**
 * Glues MissingInfoSection + PropertyInquiryForm into one unit on the
 * project page so they can share state without context or URL hash.
 *
 * Flow:
 *   1. Visitor opens an item in MissingInfoSection, clicks "Pídelo".
 *   2. We build a pre-fill message ("Me interesa recibir X..."),
 *      bump `seedNonce` to force the form to remount with the new
 *      defaultValue (textarea defaultValue doesn't sync on update —
 *      remount is the simplest reliable refresh).
 *   3. We smooth-scroll to the form anchor so the buyer lands on it
 *      with their request already typed in.
 *
 * If the project has nothing missing, the section isn't rendered and
 * the form behaves as before.
 */
export function ProjectMissingInfoAndInquiry({
  projectSlug,
  projectTitle,
  missingItems,
  currentUser,
}: Props) {
  const [seedMessage, setSeedMessage] = useState<string>("");
  const [seedNonce, setSeedNonce] = useState(0);
  const formAnchorRef = useRef<HTMLDivElement>(null);

  function handleRequest(key: MissingInfoKey) {
    setSeedMessage(buildPrefillMessage(projectTitle, key));
    setSeedNonce((n) => n + 1);
    // Defer scroll one tick so the form re-renders with the new
    // defaultValue before the viewport moves.
    setTimeout(() => {
      formAnchorRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 60);
  }

  return (
    <>
      {missingItems.length > 0 && (
        <MissingInfoSection items={missingItems} onRequest={handleRequest} />
      )}
      <div ref={formAnchorRef}>
        {currentUser ? (
          <PropertyInquiryForm
            key={seedNonce}
            propertySlug={projectSlug}
            propertyTitle={projectTitle}
            defaultMessage={seedMessage}
            defaultName={currentUser.name ?? undefined}
            defaultEmail={currentUser.email}
          />
        ) : (
          <SignInPromptCard projectSlug={projectSlug} />
        )}
      </div>
    </>
  );
}

/**
 * Anonymous-visitor replacement for the inquiry form. Strongly signals
 * that an account is required, with both signup and login paths that
 * thread the current listing URL through `returnTo` so the buyer
 * lands back on the form already authenticated.
 */
function SignInPromptCard({ projectSlug }: { projectSlug: string }) {
  const returnTo = encodeURIComponent(`/propiedad/${projectSlug}`);
  return (
    <aside
      aria-label="Consulta sobre esta propiedad"
      className="my-12 rounded-3xl bg-cream-100 p-7 ring-1 ring-ink-200 sm:p-9"
    >
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-coral">
        ¿Te interesa esta propiedad?
      </p>
      <h2 className="mt-2 text-2xl font-bold leading-tight text-ink sm:text-3xl">
        Crea tu cuenta para hacer tu consulta
      </h2>
      <p className="mt-3 text-sm leading-relaxed text-ink-700 sm:text-base">
        Solo aceptamos consultas de cuentas verificadas. Esto nos
        permite mantener un historial de cada conversación, conectarte
        con el agente, abogado y financiación, y mostrarte el estado
        de tus consultas en tu cuenta. Cero costo, cero spam.
      </p>

      <div className="mt-6 flex flex-wrap gap-3">
        <Link
          href={`/registro?returnTo=${returnTo}`}
          className="inline-flex items-center gap-2 rounded-full bg-coral px-5 py-2.5 text-sm font-semibold text-cream-50 shadow-sm transition hover:bg-coral-600"
        >
          <UserPlus className="h-4 w-4" aria-hidden />
          Crear cuenta
        </Link>
        <Link
          href={`/login?returnTo=${returnTo}`}
          className="inline-flex items-center gap-2 rounded-full border border-ink-200 bg-white px-5 py-2.5 text-sm font-semibold text-ink-700 transition hover:bg-cream-50"
        >
          <LogIn className="h-4 w-4" aria-hidden />
          Ya tengo cuenta
        </Link>
      </div>

      <p className="mt-4 text-xs text-ink-500">
        Volverás directamente a este listing una vez que entres.
      </p>
    </aside>
  );
}
